import { ElectricityDataset, ElectricityOption } from "@/api/facilities";
import {
  Form,
  FormInstance,
  FormListFieldData,
  InputNumber,
  Radio,
  Space,
  Tooltip,
  TreeSelect,
} from "antd";
import styles from "./ElectricitySource.module.scss";

import ImpactPill from "@/components/Impact/ImpactPill";
import SwitchWithLabel from "@/components/SwitchWithLabel/SwitchWithLabel";
import { useCompanySettings } from "@/hooks/useCompanySettings";
import { useGetFacility } from "@/hooks/useFacilities";
import useTranslation from "@/hooks/useTranslation";
import { MinusSquareOutlined, PlusSquareOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { useWatch } from "antd/es/form/Form";
import _ from "lodash";
import { useState } from "react";
import { multiplyImpactWithFactor } from "../../utils";
import { ElectricityContractForm, FormSubSource } from "./ElectricityContractModal";

function findChildren(options: ElectricityOption[], urn: string): ElectricityOption[] {
  const item = options.find((x) => x.urn === urn);

  let newOptions: ElectricityOption[] = item ? [item] : [];
  const children = options.filter((x) => x.parent === urn);
  children.forEach((option) => {
    newOptions = [...newOptions, ...findChildren(options, option.urn)];
  });
  return newOptions;
}

export default function ElectricitySource({
  datasets,
  options,
  field,
  form,
  estimation,
  productMass,
  facilityId,
}: {
  options?: ElectricityOption[];
  datasets?: ElectricityDataset[];
  field: FormListFieldData;
  form: FormInstance<ElectricityContractForm>;
  estimation?: boolean;
  productMass?: number;
  facilityId: number;
}) {
  const subSource: FormSubSource = form.getFieldValue("subSources")[field.key];
  const parentInfo = datasets?.find((dataset) => dataset.source_urn === subSource.parent);
  const subOptions = findChildren(options || [], subSource.parent);
  const amount = useWatch(["subSources", field.key, "amount"], form) || 0;
  const percentage = (useWatch(["subSources", field.key, "percentage"], form) || 0) / 100;
  const total = Math.round(amount * percentage * 100) / 100;
  const t = useTranslation().utilities;

  const subSourceOption = options?.find((x) => x.urn === subSource.urn);
  const subSourceInfo = datasets?.find(
    (dataset) =>
      dataset.source_urn === subSource.urn &&
      dataset.capital_owned === subSource.capital_owned &&
      dataset.voltage === subSource.voltage
  );

  const [showAdvanced, setShowAdvanced] = useState(
    subSourceInfo?.source_urn !== parentInfo?.source_urn ||
      subSourceInfo?.voltage !== "low" ||
      subSourceInfo.capital_owned
  );

  const facility = useGetFacility(facilityId);
  const company = useCompanySettings();

  if (!parentInfo || !subSourceInfo) {
    return;
  }

  return (
    <div className={styles.source}>
      <div className={styles["source-heading"]}>{_.upperFirst(parentInfo.label)}</div>
      {estimation ? (
        <>
          <div className={styles.row}>
            <span>
              <Space>
                Consumption estimate per kg
                <Tooltip title={t.consumptionEstimate}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            </span>
            <Form.Item
              key={field.key}
              name={[field.key, "amount"]}
              labelCol={{ span: 6 }}
              labelAlign="left"
              noStyle={true}
              rules={[
                {
                  required: true,
                  message: "Amount is required",
                },
              ]}
            >
              <InputNumber
                controls={false}
                addonAfter="kWh / kg"
              />
            </Form.Item>
          </div>
          <div className={styles.row}>
            <span>
              <Space>
                Total product mass in facility
                <Tooltip title={t.totalProductMassInFacility}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            </span>
            <InputNumber
              controls={false}
              addonAfter="kg"
              value={productMass}
              disabled={true}
            />
          </div>
          <div className={styles.row}>
            <span>
              <Space>
                Total consumption estimate
                <Tooltip title={t.totalConsumptionEstimate}>
                  <QuestionCircleOutlined />
                </Tooltip>
              </Space>
            </span>
            <InputNumber
              controls={false}
              addonAfter="kWh"
              value={productMass !== undefined && amount !== undefined ? productMass * amount : 0}
              disabled={true}
            />
          </div>
        </>
      ) : (
        <>
          <div className={styles.row}>
            <span>Total consumed at {facility.data?.facility_name}</span>
            <Form.Item
              key={field.key}
              name={[field.key, "amount"]}
              labelCol={{ span: 6 }}
              labelAlign="left"
              noStyle={true}
              rules={[
                {
                  required: true,
                  message: "Amount is required",
                },
              ]}
            >
              <InputNumber
                controls={false}
                addonAfter="kWh"
              />
            </Form.Item>
          </div>
          <div className={styles.row}>
            <span>Percentage for {company.data?.company_name}</span>
            <Form.Item
              key={field.key}
              name={[field.key, "percentage"]}
              labelCol={{ span: 6 }}
              labelAlign="left"
              noStyle={true}
              rules={[
                {
                  required: true,
                  message: "Percentage is required",
                },
              ]}
            >
              <InputNumber
                controls={false}
                addonAfter="%"
                min={0}
                max={100}
              />
            </Form.Item>
          </div>
          <div className={styles.row}>
            <span>Total consumed for {company.data?.company_name}</span>
            <InputNumber
              value={total}
              controls={false}
              addonAfter="kWh"
              disabled
            />
          </div>
        </>
      )}
      <div className={styles.row}>
        <div className={styles.selector}>
          <span
            className={styles["show-more"]}
            onClick={() => setShowAdvanced(!showAdvanced)}
          >
            {showAdvanced ? (
              <>
                <MinusSquareOutlined /> Hide advanced options
              </>
            ) : (
              <>
                <PlusSquareOutlined /> Show advanced options
              </>
            )}
          </span>
        </div>
        <div className={styles.impact}>
          <ImpactPill
            impact={
              subSourceInfo && total !== undefined && productMass !== undefined
                ? multiplyImpactWithFactor(
                    subSourceInfo?.impact,
                    estimation ? amount * productMass : total
                  )
                : undefined
            }
          />
        </div>
      </div>
      <div style={{ display: showAdvanced ? "block" : "none" }}>
        <div
          className={styles.row}
          style={{ display: subOptions && subOptions.length > 1 ? "flex" : "none" }}
        >
          <div>Source details</div>
          <div>
            <Form.Item
              key={field.key}
              label="Source"
              name={[field.key, "urn"]}
              noStyle={true}
              rules={[
                {
                  required: true,
                  message: "Source selection is required",
                },
              ]}
            >
              <TreeSelect
                size="small"
                className={styles["show-more-item"]}
                style={{ width: 300 }}
                treeData={subOptions?.map((option) => ({
                  id: option.urn,
                  parent: option.parent,
                  value: option.urn,
                  title: _.upperFirst(option.label),
                }))}
                treeDataSimpleMode={{ id: "id", pId: "parent" }}
                treeDefaultExpandedKeys={[parentInfo.urn]}
              />
            </Form.Item>
          </div>
        </div>
        <div className={styles.row}>
          <div>Voltage</div>
          <div>
            <Form.Item
              key={field.key}
              name={[field.key, "voltage"]}
              noStyle
              rules={[
                {
                  required: true,
                  message: "Voltage is required.",
                },
              ]}
            >
              <Radio.Group
                size="small"
                optionType="button"
                options={subSourceOption?.voltage.map((x) => ({
                  value: x,
                  label: _.capitalize(x),
                }))}
              />
            </Form.Item>
          </div>
        </div>
        <div className={styles.row}>
          <div>Capital</div>
          <div>
            <Form.Item
              key={field.key}
              name={[field.key, "capital_owned"]}
              noStyle
              valuePropName="checked"
              rules={[
                {
                  required: true,
                  message: "Capital owned is required.",
                },
              ]}
            >
              <SwitchWithLabel size="small">Owned</SwitchWithLabel>
            </Form.Item>
          </div>
        </div>
      </div>
    </div>
  );
}
